import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { logEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { exploreSampleAndGo } from "@/lib/sampleData";
import { LeadDialog } from "@/components/landing/LeadDialog";
import { toast } from "sonner";
import {
  ChatCircleDots, X, PaperPlaneTilt, Sparkle, UploadSimple, PlusCircle, Headset, ArrowLeft,
} from "@phosphor-icons/react";

const JK = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

// Zero-dependency safe formatter: renders ONLY **bold**, line breaks and "- " bullets as React
// elements. Never uses dangerouslySetInnerHTML / never parses HTML — so raw HTML or LLM-invented
// links in the text stay inert, escaped text. Anything unrecognized renders as ordinary text.
function renderInline(text, keyBase) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const m = p.match(/^\*\*([^*]+)\*\*$/);
    if (m) return <strong key={`${keyBase}-b${i}`}>{m[1]}</strong>;
    return <span key={`${keyBase}-t${i}`}>{p}</span>;
  });
}

function FormattedText({ text }) {
  const lines = String(text || "").replace(/\r/g, "").split("\n");
  const blocks = [];
  let bullets = null;
  lines.forEach((line, idx) => {
    const bm = line.match(/^\s*[-•]\s+(.*)$/);
    if (bm) {
      bullets = bullets || [];
      bullets.push(<li key={`li-${idx}`}>{renderInline(bm[1], `li-${idx}`)}</li>);
    } else {
      if (bullets) { blocks.push(<ul key={`ul-${idx}`} className="list-disc pl-5 my-1 space-y-0.5">{bullets}</ul>); bullets = null; }
      if (line.trim() !== "") blocks.push(<p key={`p-${idx}`} className="my-0.5">{renderInline(line, `p-${idx}`)}</p>);
    }
  });
  if (bullets) blocks.push(<ul key="ul-last" className="list-disc pl-5 my-1 space-y-0.5">{bullets}</ul>);
  return <>{blocks}</>;
}

function chatSessionId() {
  try {
    let id = localStorage.getItem("ros_sid");
    if (!id) {
      id = `sid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("ros_sid", id);
    }
    return id;
  } catch { return "sid_unknown"; }
}

export default function ChatWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [escalate, setEscalate] = useState(false);
  const [lead, setLead] = useState({ open: false, kind: "contact" });
  const scrollRef = useRef(null);
  const sid = chatSessionId();

  // Don't render on the full-screen onboarding/auth flows (avoid covering primary CTAs).
  const hidePaths = ["/welcome", "/change-password", "/reset-password"];
  const hidden = hidePaths.some((p) => location.pathname.startsWith(p)) || location.pathname === "/login" || location.pathname === "/signin";

  const loadContext = useCallback(() => {
    api.get("/assistant/context", { params: { page: location.pathname } })
      .then((r) => setCtx(r.data))
      .catch(() => setCtx({ mode: user ? "activated" : "public", suggested_prompts: ["How does RecoverOS work?", "How do I upload my Excel?", "Pricing & plans", "Talk to a person"], escalation_prefill: {}, activation_state: "n/a" }));
  }, [location.pathname, user]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, busy, open]);

  useEffect(() => {
    const onOpen = () => openChat();
    window.addEventListener("recoveros:open-chat", onOpen);
    return () => window.removeEventListener("recoveros:open-chat", onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, msgs.length]);

  const openChat = () => {
    setOpen(true);
    logEvent("chat_opened", { page: location.pathname, logged_in: !!user });
    if (!ctx) loadContext();
    if (msgs.length === 0) {
      setMsgs([{ role: "assistant", text: "Hi — I'm the RecoverOS Assistant. Ask me anything about RecoverOS, or pick a suggestion below." }]);
    }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setEscalate(false);
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: sid, message: q, page: location.pathname });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply, actions: Array.isArray(data.actions) ? data.actions : [] }]);
      logEvent("chat_message_sent", { category: data.category, mode: data.mode });
    } catch (e) {
      const status = e?.response?.status;
      setMsgs((m) => [...m, { role: "assistant", text: status === 429 ? "You're sending messages a bit too fast — please wait a few seconds and try again." : "Something went wrong. You can email support@recoveros.in or talk to a person." }]);
    } finally {
      setBusy(false);
    }
  };

  // Safe, user-initiated navigation actions only.
  const doAction = (action) => {
    if (action === "upload_excel") { setOpen(false); navigate("/import"); logEvent("chat_action", { action }); }
    else if (action === "add_one_account") { setOpen(false); window.dispatchEvent(new CustomEvent("recoveros:quick-add", { detail: { source: "assistant" } })); logEvent("chat_action", { action }); }
    else if (action === "sample") { setOpen(false); exploreSampleAndGo(toast); logEvent("chat_action", { action }); }
  };

  // Allow-listed CTA buttons returned by the server (key -> approved route/handler). Unknown keys ignored.
  const ACTION_META = {
    upload_excel: { label: "Upload Excel", run: () => { setOpen(false); navigate("/import"); } },
    add_account: { label: "Add one account", run: () => { setOpen(false); window.dispatchEvent(new CustomEvent("recoveros:quick-add", { detail: { source: "assistant" } })); } },
    download_template: { label: "Download template", run: () => { setOpen(false); navigate("/import"); } },
    explore_sample: { label: "Explore sample data", run: () => { setOpen(false); exploreSampleAndGo(toast); } },
    view_pricing: { label: "View plans", run: () => { setOpen(false); if (user) { navigate("/subscription"); } else { navigate("/pricing"); } } },
    start_trial: { label: "Start with Intro PAYG", run: () => { setOpen(false); navigate("/signup"); } },
    book_demo: { label: "Book a demo", run: () => openEscalation("demo") },
    request_callback: { label: "Request a callback", run: () => openEscalation("callback") },
    email_support: { label: "Email support", run: () => openEscalation("email") },
  };

  const runActionKey = (key) => {
    const meta = ACTION_META[key];
    if (!meta) return; // ignore unknown/unsafe keys
    logEvent("chat_action", { action: key });
    meta.run();
  };

  const openEscalation = (channel) => {
    logEvent("chat_escalated", { channel });
    if (channel === "email") { window.location.href = "mailto:support@recoveros.in?subject=RecoverOS%20Support"; return; }
    setLead({ open: true, kind: channel === "demo" ? "demo" : "contact" });
  };

  if (hidden) return null;

  const unactivated = ctx?.mode === "unactivated";
  const showActionRow = unactivated;

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          onClick={openChat}
          data-testid="chat-launcher"
          className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full bg-[#0B3D91] text-white pl-3 pr-4 py-3 shadow-lg hover:bg-[#0a347d] hover:-translate-y-0.5 transition-[transform,background-color]"
          aria-label="Chat with RecoverOS"
        >
          <ChatCircleDots size={22} weight="fill" />
          <span className="text-sm font-semibold hidden sm:inline" style={JK}>Chat with RecoverOS</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-40 bg-white border border-slate-200 shadow-2xl flex flex-col
            inset-0 sm:inset-auto sm:bottom-4 sm:right-4 sm:w-[380px] sm:h-[600px] sm:max-h-[85vh] sm:rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-[#0B3D91] text-white shrink-0">
            <div className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center"><Sparkle size={18} weight="fill" /></div>
            <div className="flex-1">
              <div className="text-sm font-bold leading-tight" style={JK}>RecoverOS Assistant</div>
              <div className="text-[11px] text-white/70">Available 24×7 · AI assistant</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="text-white/80 hover:text-white" aria-label="Close chat"><X size={20} weight="bold" /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="chat-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div data-testid={`chat-msg-${m.role}`} className={`max-w-[85%] text-sm leading-relaxed px-3 py-2 rounded-2xl ${m.role === "user" ? "bg-[#0B3D91] text-white rounded-br-sm whitespace-pre-wrap" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                  {m.role === "assistant" ? <FormattedText text={m.text} /> : m.text}
                </div>
                {m.role === "assistant" && Array.isArray(m.actions) && m.actions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 max-w-[90%]" data-testid="chat-actions">
                    {m.actions.map((key) => ACTION_META[key] ? (
                      <button key={key} onClick={() => runActionKey(key)} data-testid={`chat-action-${key}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg border-2 border-[#0B3D91] text-[#0B3D91] px-3 py-1.5 hover:bg-[#0B3D91] hover:text-white transition-colors">
                        {ACTION_META[key].label}
                      </button>
                    ) : null)}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-slate-400" data-testid="chat-typing">Assistant is typing…</div></div>
            )}

            {/* Suggested prompts (only before the first user message) */}
            {ctx && msgs.filter((m) => m.role === "user").length === 0 && !busy && (
              <div className="pt-1 space-y-1.5" data-testid="chat-suggestions">
                {(ctx.suggested_prompts || []).map((p) => (
                  <button key={p} onClick={() => (p.toLowerCase().includes("talk to a person") ? setEscalate(true) : send(p))}
                    className="block w-full text-left text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-[#0B3D91] hover:text-[#0B3D91] transition-colors"
                    data-testid="chat-suggestion">
                    {p}
                  </button>
                ))}
              </div>
            )}

            {/* Escalation options */}
            {escalate && (
              <div className="pt-1 space-y-1.5 border-t border-slate-200 mt-2" data-testid="chat-escalation">
                <div className="text-xs font-semibold text-slate-500 pt-2">Talk to a person</div>
                <button onClick={() => openEscalation("callback")} className="block w-full text-left text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-[#0B3D91]" data-testid="chat-escalate-callback">Request a callback</button>
                <button onClick={() => openEscalation("demo")} className="block w-full text-left text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-[#0B3D91]" data-testid="chat-escalate-demo">Book a demo</button>
                <button onClick={() => openEscalation("email")} className="block w-full text-left text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-[#0B3D91]" data-testid="chat-escalate-email">Email support@recoveros.in</button>
              </div>
            )}
          </div>

          {/* Activation action row (unactivated logged-in users) */}
          {showActionRow && (
            <div className="flex items-center gap-2 px-3 py-2 border-t border-slate-200 bg-white shrink-0" data-testid="chat-action-row">
              <button onClick={() => doAction("upload_excel")} data-testid="chat-action-upload" className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold rounded-lg bg-[#16A34A] text-white px-2 py-2 hover:bg-[#138a3e]">
                <UploadSimple size={14} weight="bold" /> Upload Excel
              </button>
              <button onClick={() => doAction("add_one_account")} data-testid="chat-action-add-one" className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold rounded-lg border-2 border-[#0B3D91] text-[#0B3D91] px-2 py-2 hover:bg-[#0B3D91]/[0.05]">
                <PlusCircle size={14} weight="bold" /> Add one account
              </button>
            </div>
          )}

          {/* Input */}
          <div className="flex items-end gap-2 p-3 border-t border-slate-200 bg-white shrink-0">
            {!escalate ? (
              <button onClick={() => setEscalate(true)} title="Talk to a person" data-testid="chat-talk-to-person" className="h-9 w-9 shrink-0 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:text-[#0B3D91] hover:border-[#0B3D91]">
                <Headset size={18} weight="bold" />
              </button>
            ) : (
              <button onClick={() => setEscalate(false)} title="Back" data-testid="chat-escalate-back" className="h-9 w-9 shrink-0 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:text-[#0B3D91]">
                <ArrowLeft size={18} weight="bold" />
              </button>
            )}
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about RecoverOS…"
              rows={1}
              maxLength={1000}
              data-testid="chat-input"
              className="flex-1 resize-none text-sm border border-slate-200 rounded-lg px-3 py-2 max-h-24 focus:outline-none focus:border-[#0B3D91]"
            />
            <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="chat-send" className="h-9 w-9 shrink-0 rounded-lg bg-[#0B3D91] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#0a347d]">
              <PaperPlaneTilt size={18} weight="fill" />
            </button>
          </div>
        </div>
      )}

      <LeadDialog open={lead.open} onOpenChange={(o) => setLead((s) => ({ ...s, open: o }))} kind={lead.kind} prefill={ctx?.escalation_prefill || null} />
    </>
  );
}
